import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { ApiError } from './api/client.js';
import { redirectToSignIn, SESSION_QUERY_KEY } from '../features/auth/session.js';

/**
 * One query client for the app.
 *
 * Retrying a 4xx is pointless - there is no refresh flow any more (session is
 * a plain cookie, checked by `GET /auth/me`), and a 401 there simply means
 * "no session". Only 5xx and network faults are retried.
 */
export const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: handleSessionExpiry }),
  mutationCache: new MutationCache({ onError: handleSessionExpiry }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        if (error instanceof ApiError && error.status < 500) return false;
        return failureCount < 2;
      },
    },
    mutations: {
      retry: false,
    },
  },
});

/**
 * ui-contract.md section 6 (SH-25): a session that ends mid-work answers any
 * request with UNAUTHENTICATED. Once this tab holds a session, that sends the
 * employee to sign in again and back to this page instead of showing the error
 * inside a form. Before a session exists (the launch screen) the
 * screen that made the request handles the 401 itself; other 401 codes are
 * business errors shown in place.
 */
function handleSessionExpiry(error: unknown): void {
  if (!(error instanceof ApiError) || error.code !== 'UNAUTHENTICATED') return;
  if (queryClient.getQueryData(SESSION_QUERY_KEY) === undefined) return;
  redirectToSignIn();
}
